//
// Copyright (c) 2011-2020 by Curalate, Inc.
//

import React from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { Button, SearchBar, Select, SelectItem } from 'unison';

import LoadingBar from '@components/LoadingBar';
import { HelpIconWithMargin } from '@components/atoms/help-icon/HelpIcon';
import { useBoundActionCreators } from '@hooks/useCuralateStore';
import { streamCreationActions } from '@streams/actions/streamCreationActions';
import InstagramLinkoutButton from '@streams/components/InstagramLinkoutButton';
import { StreamSourcesList } from '@streams/components/StreamSourcesList';
import {
  StreamSourcePillType,
  StreamSourceTypeLabels,
  StreamSourceTypes,
  streamSourceTypeToContentSourceType,
} from '@streams/constants/streamCreationConstants';
import { IStreamCreationErrors } from '@streams/models/streamCreationState';
import { Mixins } from '@theme/mixins';

interface IExclusionSourceTypeSelectItem {
  label: string;
  value: StreamSourceTypes;
}

type streamLabels = keyof typeof StreamSourceTypeLabels;
const StreamCreationSourceTypes: streamLabels[] = [
  StreamSourceTypes.HASHTAG,
  StreamSourceTypes.USER,
];

export const StreamsExcludedSourceSelector = () => {
  const excludedSources = useSelector(
    (state) => state.streams.streamCreation.exclusionSourcesAdded,
  );
  const hasOutstandingSaveRequest = useSelector(
    (state) => state.streams.streamCreation.hasOutstandingSaveRequest,
  );
  const isLoadingSources = useSelector(
    (state) => state.streams.streamCreation.isLoadingSources,
  );
  const errors = useSelector((state) => state.streams.streamCreation.errors);
  const { sourceType, sourceValue } = useSelector(
    (state) => state.streams.streamCreation.exclusionSourceSelector,
  );

  const {
    addExclusionSource,
    changeExclusionSourceType,
    changeExclusionSourceValue,
    removeExclusionSource,
  } = useBoundActionCreators(streamCreationActions);

  const handleSourceValueChange = (e: React.FormEvent<HTMLInputElement>) => {
    changeExclusionSourceValue(e.currentTarget.value);
  };

  const contentSourceType = streamSourceTypeToContentSourceType(sourceType);
  const sourceTypeSelectItems = computeExclusionSourceTypeSelectItems();
  const hasExclusionSourceErrors =
    errors.exclusionSourceError || errors.exclusionSourceTypeError;

  let placeholderText = '';
  switch (sourceType) {
    case StreamSourceTypes.HASHTAG:
      placeholderText = 'Enter a hashtag';
      break;
    case StreamSourceTypes.USER:
      placeholderText = 'Enter a username';
      break;
    default:
      placeholderText = 'Enter a username or hashtag';
  }

  const handleAdd = () => {
    if (sourceValue) {
      addExclusionSource();
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleAdd();
    }
  };

  return (
    <ExcludedSourceContainer>
      <ExcludeContentHeader>
        Exclude Content from Stream
        <HelpIconWithMargin helpText='Add sources below to stop content from being added to this Stream (Instagram Only).' />
      </ExcludeContentHeader>
      <div className='cu-d-flex cu-mb-2'>
        <StreamSourceSelectWrapper className='cu-mr-2'>
          <TypeHeader>Type</TypeHeader>
          <Select
            className='cu-w-100'
            handleChange={changeExclusionSourceType}
            isDisabled={hasOutstandingSaveRequest}
            items={sourceTypeSelectItems}
            unselectedLabel='Select a Source Type'
            value={sourceType}
          >
            {renderExclusionSelectItems(sourceTypeSelectItems)}
          </Select>
        </StreamSourceSelectWrapper>
        <ExcludedSourceInputWrapper>
          <ExcludedSourceValueContainer>
            <SourceHeader>
              Source
              {sourceType && contentSourceType && sourceValue && (
                <InstagramLinkoutButton
                  sourceName={sourceValue}
                  sourceType={contentSourceType}
                />
              )}
            </SourceHeader>
            <SearchBar className='cu-w-100'>
              <FullWidthInputWrapper>
                <input
                  className='cu-form-control'
                  disabled={hasOutstandingSaveRequest}
                  onChange={handleSourceValueChange}
                  onKeyPress={onKeyDown}
                  placeholder={placeholderText}
                  type='text'
                  value={sourceValue}
                />
              </FullWidthInputWrapper>
            </SearchBar>
          </ExcludedSourceValueContainer>
          <Button
            className='cu-button-secondary'
            isDisabled={hasOutstandingSaveRequest || !sourceValue}
            onClick={handleAdd}
          >
            Add
          </Button>
        </ExcludedSourceInputWrapper>
      </div>

      <LoadingBar className='cu-mt-3' shouldShowLoader={isLoadingSources}>
        {hasExclusionSourceErrors ? (
          renderErrors(errors)
        ) : (
          <StreamSourcesList
            addedSources={excludedSources}
            defaultPillType={StreamSourcePillType.EXCLUDED_SOURCE}
            hasOutstandingSaveRequest={hasOutstandingSaveRequest}
            removeSource={removeExclusionSource}
          />
        )}
      </LoadingBar>
    </ExcludedSourceContainer>
  );
};

const computeExclusionSourceTypeSelectItems = () => {
  return StreamCreationSourceTypes.map((type) => {
    return {
      label: StreamSourceTypeLabels[type],
      value: type,
    } as const;
  });
};

const renderExclusionSelectItems = (
  itemsList: IExclusionSourceTypeSelectItem[],
) => {
  return itemsList.map((item) => {
    return (
      <SelectItem key={item.value} label={item.label} value={item.value} />
    );
  });
};

const renderErrors = (errors: IStreamCreationErrors) => {
  return (
    <>
      {errors.exclusionSourceTypeError && (
        <Warning>{errors.exclusionSourceTypeError}</Warning>
      )}
      {errors.exclusionSourceError && (
        <Warning>{errors.exclusionSourceError} </Warning>
      )}
    </>
  );
};

const StreamSourceSelectWrapper = styled.div`
  flex: 2;
  .cu-popover-target {
    width: 100%;
  }
`;

const TypeHeader = styled.div`
  ${Mixins.Text.Body}
  margin-bottom: ${({ theme }) => theme.spacings[2]};
`;

const FullWidthInputWrapper = styled.div`
  input {
    width: 100%;
  }
`;

const ExcludedSourceContainer = styled.div`
  margin-top: ${({ theme }) => theme.spacings[5]};
`;

const ExcludeContentHeader = styled.div`
  ${Mixins.Text.UiCopyBold}
  margin-bottom: ${({ theme }) => theme.spacings[2]};
  display: flex;
  align-items: center;
`;

const ExcludedSourceInputWrapper = styled.div`
  flex: 3;
  display: flex;
  align-items: flex-end;
`;

const ExcludedSourceValueContainer = styled.div`
  margin-right: ${({ theme }) => theme.spacings[2]};
  flex-grow: 3;
`;

// Span hack ensures text overflow works properly on the InstagramLinkoutButton
const SourceHeader = styled.div`
  ${Mixins.Text.Body}
  margin-bottom: ${({ theme }) => theme.spacings[2]};
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 470px;

  span {
    min-width: 0;
  }
`;

const Warning = styled.div`
  color: ${({ theme }) => theme.colors.aliases.error};
  margin-bottom: ${({ theme }) => theme.spacings[1]};
`;

export default StreamsExcludedSourceSelector;
